import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { trackEvent } from '@/config/analytics';
import { inventoryService } from '@/services/inventoryService';
import { deleteLabelImage } from '@/services/storageService';
import { showToast } from '@/components/rc';
import { CONFIG } from '@/constants';
import { getMaturityRank } from '@/utils/maturityUtils';
import { useProfile } from '@/context/ProfileContext';
import { convertToHome } from '@/lib/currencyConversion';
import { ScanHandoffProvider, ScanHandoffContext } from '@/context/ScanHandoffContext';
import type { ScanHandoffContextValue } from '@/context/ScanHandoffContext';
import type { Wine, SortField, FacetKey } from '@/types';
import type { FiltersState, FacetOption } from '@/lib/faceted-filters';
import {
  EMPTY_FILTERS,
  matchesAllFacets,
  aggregateFacetOptions,
  countActiveFilters,
  type PriceContext,
} from '@/lib/faceted-filters';

// ── Dynamic facet options shape ──
interface FacetOptionsMap {
  [key: string]: FacetOption[];
  wineType: FacetOption[];
  maturityStatus: FacetOption[];
  country: FacetOption[];
  region: FacetOption[];
  appellation: FacetOption[];
  location: FacetOption[];
  producer: FacetOption[];
  grapeVariety: FacetOption[];
  vintage: FacetOption[];
  price: FacetOption[];
}

// ── Core inventory context value (without scan/handoff) ──
interface CoreInventoryContextValue {
  // Core data
  inventory: Wine[];
  displayInventory: Wine[];
  isOverFreeLimit: boolean;
  loading: boolean;
  isSynced: boolean;

  // Search + filters + sort
  search: string;
  setSearch: (s: string) => void;
  filters: FiltersState;
  facetOptions: FacetOptionsMap;
  filteredInventory: Wine[];
  toggleFacet: (key: FacetKey, value: string) => void;
  clearFilters: () => void;
  activeFilterCount: number;
  totalBottlesFiltered: number;
  heroWineIds: string[];
  sortField: SortField;
  setSortField: (f: SortField) => void;

  // Unique locations for combobox suggestions
  uniqueLocations: string[];

  // Bottle cap
  totalBottles: number;
  canAddBottles: (count?: number) => boolean;

  // Wine CRUD
  handleUpdate: (wine: Wine, key: string, value: string) => Promise<void>;
  handleDeleteWine: (wineId: string) => Promise<void>;
}

// ── Full context value shape (core + scan/handoff) — unchanged public API ──
type InventoryContextValue = CoreInventoryContextValue & ScanHandoffContextValue;

const InventoryContext = createContext<CoreInventoryContextValue | null>(null);

export function useInventory(): InventoryContextValue {
  const core = useContext(InventoryContext);
  const sh = useContext(ScanHandoffContext);
  if (!core) throw new Error('useInventory must be used within InventoryProvider');
  if (!sh) throw new Error('useInventory must be used within ScanHandoffProvider');
  return { ...core, ...sh };
}

const FACET_KEYS: readonly FacetKey[] = [
  'wineType', 'maturityStatus', 'country', 'region', 'appellation',
  'location', 'producer', 'grapeVariety', 'vintage', 'price',
] as const;

const NUMERIC_WINE_FIELDS = new Set(['vintage', 'quantity', 'drinkFrom', 'drinkUntil', 'criticRating', 'price']);

function maturityRank(wine: Wine): number {
  return getMaturityRank(wine.drinkFrom, wine.drinkUntil);
}

export const InventoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isPremium, profile } = useProfile();

  // Refs for ScanHandoffProvider to register callbacks (selectedWine sync)
  const onWineDeletedRef = useRef<((wineId: string) => void) | null>(null);
  const onWineUpdatedRef = useRef<((wineId: string, key: string, coerced: any) => void) | null>(null);

  // ── Core state ──
  const [inventory, setInventory] = useState<Wine[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSynced, setIsSynced] = useState(false);

  // ── Search + filter + sort state ──
  const [search, setSearchRaw] = useState('');
  const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);
  const [sortField, setSortField] = useState<SortField>('maturity');

  // Sync search into filters.searchQuery
  const setSearch = useCallback((s: string) => {
    setSearchRaw(s);
    setFilters(prev => ({ ...prev, searchQuery: s }));
  }, []);

  // ── Real-time Firestore listener ──
  useEffect(() => {
    const unsubscribe = inventoryService.onInventoryChange((wines) => {
      setInventory(wines);
      setLoading(false);
      setIsSynced(true);
    });
    return () => unsubscribe();
  }, []);

  const { currency: homeCurrency, conversionRates } = profile;

  // ── Free-tier slicing (by bottle count, not wine count) ──
  const displayInventory = useMemo(() => {
    if (isPremium) return inventory;
    const max = CONFIG.FREE_TIER.MAX_BOTTLES;
    // Walk backwards (most recent first) and include wines until bottle cap is reached
    let bottleCount = 0;
    let cutIndex = inventory.length;
    for (let i = inventory.length - 1; i >= 0; i--) {
      const qty = Number(inventory[i].quantity) || 1;
      if (bottleCount + qty > max) { cutIndex = i + 1; break; }
      bottleCount += qty;
      cutIndex = i;
    }
    return inventory.slice(cutIndex);
  }, [inventory, isPremium]);

  const isOverFreeLimit = !isPremium && displayInventory.length < inventory.length;

  // ── Price context for currency-aware filters ──
  const priceCtx = useMemo<PriceContext>(
    () => ({ homeCurrency, rates: conversionRates }),
    [homeCurrency, conversionRates],
  );

  // ── Faceted filter logic (operates on displayInventory) ──
  const filteredInventory = useMemo(
    () => displayInventory.filter(w => matchesAllFacets(w, filters, priceCtx)),
    [displayInventory, filters, priceCtx],
  );

  const sortedInventory = useMemo(() => {
    const sorted = [...filteredInventory];
    const hp = (w: Wine) => convertToHome(Number(w.price) || 0, w.priceCurrency, homeCurrency, conversionRates);
    sorted.sort((a, b) => {
      switch (sortField) {
        case 'maturity':
          return maturityRank(a) - maturityRank(b);
        case 'vintage-desc':
        case 'vintage-asc': {
          // NV wines have no meaningful year — bucket them with nulls
          const aV = a.isNV ? null : (a.vintage != null ? Number(a.vintage) : null);
          const bV = b.isNV ? null : (b.vintage != null ? Number(b.vintage) : null);

          // Push nulls (NV wines) to the end regardless of sort direction
          if (aV === null && bV === null) return a.producer.localeCompare(b.producer);
          if (aV === null) return 1;
          if (bV === null) return -1;

          // If NaN after coercion, also push to end
          if (isNaN(aV) && isNaN(bV)) return a.producer.localeCompare(b.producer);
          if (isNaN(aV)) return 1;
          if (isNaN(bV)) return -1;

          // Primary: vintage comparison
          const direction = sortField === 'vintage-asc' ? 1 : -1;
          const diff = (aV - bV) * direction;

          // Secondary: stable sort by producer when vintages match
          return diff !== 0 ? diff : a.producer.localeCompare(b.producer);
        }
        case 'rating':
          return (b.criticRating || 0) - (a.criticRating || 0);
        case 'price-desc':
          return hp(b) - hp(a);
        case 'price-asc':
          return hp(a) - hp(b);
        case 'producer':
          return a.producer.localeCompare(b.producer);
        case 'country':
          return a.country.localeCompare(b.country);
        default:
          return 0;
      }
    });
    return sorted;
  }, [filteredInventory, sortField, homeCurrency, conversionRates]);

  // ── Dynamic self-excluding facet options (from displayInventory) ──
  const facetOptions = useMemo<FacetOptionsMap>(() => {
    const result = {} as FacetOptionsMap;
    for (const key of FACET_KEYS) {
      result[key] = aggregateFacetOptions(displayInventory, filters, key, priceCtx);
    }
    return result;
  }, [displayInventory, filters, priceCtx]);

  const toggleFacet = useCallback((key: FacetKey, value: string) => {
    setFilters(prev => {
      const facet = prev[key];
      if ('include' in facet) {
        const arr = facet.include;
        const next = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
        return { ...prev, [key]: { ...facet, include: next } };
      }
      return prev;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setSearchRaw('');
    setSortField('maturity');
  }, []);

  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);

  const totalBottlesFiltered = useMemo(
    () => sortedInventory.reduce((sum, w) => sum + (Number(w.quantity) || 0), 0),
    [sortedInventory],
  );

  const uniqueLocations = useMemo(
    () => Array.from(new Set(displayInventory.map(w => w.location).filter((l): l is string => !!l))).sort(),
    [displayInventory],
  );

  const heroWineIds = useMemo(
    () => [...sortedInventory].sort((a, b) => (b.criticRating || 0) - (a.criticRating || 0)).slice(0, 2).map(w => w.id),
    [sortedInventory],
  );

  // ── Bottle cap ──
  const totalBottles = useMemo(
    () => inventory.reduce((sum, w) => sum + (Number(w.quantity) || 0), 0),
    [inventory],
  );

  const canAddBottles = useCallback((count: number = 1): boolean => {
    if (isPremium) return true;
    return totalBottles + count <= CONFIG.FREE_TIER.MAX_BOTTLES;
  }, [isPremium, totalBottles]);

  // ── Cellar milestone tracking ──
  const prevTotalRef = useRef(totalBottles);
  useEffect(() => {
    const prev = prevTotalRef.current;
    prevTotalRef.current = totalBottles;
    const milestones = [3, 10, 50];
    for (const m of milestones) {
      if (prev < m && totalBottles >= m) {
        trackEvent('cellar_milestone', { milestone: m, total: totalBottles });
      }
    }
  }, [totalBottles]);

  // ── Wine delete handler (standalone, also used by qty→0) ──
  const handleDeleteWine = useCallback(async (wineId: string) => {
    const wine = inventory.find(w => w.id === wineId);
    const wineName = wine ? `${wine.vintage || ''} ${wine.producer || 'Wine'}`.trim() : 'Wine';
    await inventoryService.deleteWine(wineId);
    deleteLabelImage(wineId).catch(() => {});
    setInventory(prev => prev.filter(w => w.id !== wineId));
    onWineDeletedRef.current?.(wineId);
    showToast({ tone: 'neutral', message: `${wineName} removed from cellar` });
  }, [inventory]);

  // ── Wine update handler ──
  const handleUpdate = useCallback(async (wine: Wine, key: string, value: string) => {
    // Bottle cap check on quantity increase
    if (key === 'quantity') {
      const newQty = Number(value);
      const currentQty = Number(wine.quantity) || 0;
      const delta = newQty - currentQty;
      if (delta > 0 && !canAddBottles(delta)) {
        showToast({ tone: 'warning', message: `Free plan is limited to ${CONFIG.FREE_TIER.MAX_BOTTLES} bottles. Upgrade to add more.` });
        return;
      }
    }

    // Auto-delete when quantity reaches 0
    if (key === 'quantity' && Number(value) <= 0) {
      await handleDeleteWine(wine.id);
      return;
    }

    // grapeVarieties is passed as JSON string from WineModal — parse back to array
    // isNV is passed as 'true' / '' — coerce to boolean
    const actualValue = key === 'grapeVarieties'
      ? JSON.parse(value)
      : key === 'isNV'
        ? value === 'true'
        : value;
    const success = await inventoryService.updateField(wine.id, key, actualValue);
    if (success) {
      const coerced: any = key === 'grapeVarieties'
        ? actualValue
        : key === 'isNV'
          ? actualValue
          : NUMERIC_WINE_FIELDS.has(key) ? Number(value) : value;
      setInventory(prev => prev.map(w => w.id === wine.id ? { ...w, [key]: coerced } : w));
      onWineUpdatedRef.current?.(wine.id, key, coerced);
    }
  }, [canAddBottles, handleDeleteWine]);

  const value = useMemo<CoreInventoryContextValue>(() => ({
    inventory,
    displayInventory,
    isOverFreeLimit,
    loading,
    isSynced,
    search,
    setSearch,
    filters,
    facetOptions,
    filteredInventory: sortedInventory,
    toggleFacet,
    clearFilters,
    activeFilterCount,
    totalBottlesFiltered,
    heroWineIds,
    sortField,
    setSortField,
    uniqueLocations,
    totalBottles,
    canAddBottles,
    handleUpdate,
    handleDeleteWine,
  }), [
    inventory, displayInventory, isOverFreeLimit, loading, isSynced, search, setSearch, filters, facetOptions,
    sortedInventory, sortField, toggleFacet, clearFilters, activeFilterCount,
    totalBottlesFiltered, heroWineIds, uniqueLocations, totalBottles, canAddBottles, handleUpdate, handleDeleteWine,
  ]);

  return (
    <InventoryContext.Provider value={value}>
      <ScanHandoffProvider canAddBottles={canAddBottles} setIsSynced={setIsSynced} onWineDeletedRef={onWineDeletedRef} onWineUpdatedRef={onWineUpdatedRef}>
        {children}
      </ScanHandoffProvider>
    </InventoryContext.Provider>
  );
};
